// Webinars (AdminWebinars.dc.html): every webinar, Coming up and Replays, with Add a webinar, and the
// one picked or being added: what's typed about it (the board's From Zoom box, typed by hand since the
// hub never connects to Zoom: Adam, 5 Oct, stage 6), the hub's own fields (shown, what it sells, the
// call to action's words, link and line, the replay link and its chapters, up or down) and the hub's
// own count, the replay watched. Owner and admin add and change them; support reads (data-model 8.2).
// Switched on while still to come, the saved line says it's in every account's bell only when
// hub.bell_will_ring said so before the write (Adam, 5 Oct, stage 6's batch). Where the list and the
// editor stack (980px and under), picking a webinar or Add a webinar scrolls to the editor (item 30).
// On a laptop, picking 1 while scrolled down brings the editor's top back into view.
// Each webinar can have Adam's own thumbnail (Adam, 9 Oct), between what's typed and Show it: picked,
// redrawn in the browser at up to 1280 by 720, uploaded straight away into the covers bucket, and put on
// the webinar with Save (or Add it). Take it off saves none; the daily clear-up removes the file later.
// The list shows each 1 small where any webinar has 1. The block and its previews are ThumbnailBlock.jsx's,
// shared with the recording editor since 12 Oct (decision 47). As on Recordings (Adam, 13 Oct), the list
// stays beside the editor on a laptop, scrolling in its own box, and the editor's foot (Save or Add it,
// Cancel, the saved line and any problem) is pinned to the bottom of the screen, so the thumbnail and
// Save are on screen together.
// Each saved webinar has its register page (docs/replay-register-spec.md 5, Adam's cut-down version of 23 Oct)
// under the replay: its link, its name in MailerLite and its switch (ReplayRegister.jsx), saved with Save,
// which writes those fields only when they changed, and Registrations (n) for owner and admin.
import { useEffect, useId, useRef, useState } from 'react'
import { Button, DateField, Dot, EmptyState, Field, InfoNote, Select, TextArea, TextInput, TimeField, ukDayOf, ukToIso } from '../ui/index.js'
import { longDate, shortDate, time, duration, uk } from '../../lib/format.js'
import { useStacked } from '../../lib/media.js'
import { COPY } from './copy.js'
import { LinkNote } from './Head.jsx'
import {
  webinarGroups, webinarState, webinarCount, webinarWhen, webinarWhenLong, webinarTiles, isPast, sellOptions,
  webinarProblem, webinarDetailsProblem, webinarSavedLine, chaptersFromText, chaptersToText, readChapterLines,
  parseLength, lengthOptions, registrationLink, showsWithoutRegistration, switchesOnAhead, webinarProductOf,
  thumbnailFileProblem, typedSlug, listNameFromSlug, registerOnProblem, registerProblem, registerPatch,
} from './model.js'
import { useVimeoRead } from './vimeoRead.js'
import { addWebinar, saveWebinar, shrinkImage, uploadThumbnail } from './data.js'
import { ThumbnailBlock as Thumbnail, uploaded, usePreviews } from './ThumbnailBlock.jsx'
import { RegisterPart } from './ReplayRegister.jsx'
import { ButtonLink, adminPath } from '../ui/index.js'
import { ENV } from '../../lib/env.js'
import { strategyOpen } from '../../strategy/route.js'

const OA = role => role === 'owner' || role === 'admin'
const hhmm = iso => { if (!iso) return ''; const u = uk(iso); return `${String(u.hh).padStart(2, '0')}:${String(u.mm).padStart(2, '0')}` }
// A new webinar's length until another is picked: an hour, as most of the boards' are
const NEW_MINUTES = 60

// The dot for what a webinar sells: the product's colour, or a white ring for nothing
function SellDot({ product }) {
  return product ? <Dot product={product} ringed /> : <Dot colour="var(--shell-bg)" ringed />
}

export function WebinarsView({ data, reload, api, role, personId, initialId }) {
  const nowMs = Date.now()
  const { webinars, products, stats, registerCounts } = data
  const statBy = new Map((stats || []).map(s => [s.webinar_id, s]))
  const registeredBy = new Map((registerCounts || []).map(c => [c.webinar_id, c.registrations]))
  const productBy = new Map(products.map(p => [p.id, p]))
  const groups = webinarGroups(webinars, nowMs)
  const all = groups.flatMap(g => g.items)
  const [selId, setSelId] = useState(initialId || null)
  const [adding, setAdding] = useState(false)
  const [version, setVersion] = useState(0)
  const [flash, setFlash] = useState(null)
  const sel = adding ? null : (all.find(w => w.id === selId) || all[0] || null)
  const edits = OA(role)
  const editor = useRef(null)
  // the list and the editor stack at 980px and under (admin-content.css), so the editor sits under the list
  const stacked = useStacked()
  const preview = usePreviews(api, webinars)
  const anyThumb = webinars.some(w => w.thumbnail_path)

  // still on this screen: a save that lands after Adam has left never moves the address
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  // the address follows, so a reload opens the same webinar; nothing else on the page changes
  const follow = to => { try { window.history.replaceState({}, '', to) } catch { /* not a browser history */ } }
  // the editor's top into view where it's off screen (picked from the side column scrolled down) or
  // stacked under the list, as Recordings does
  const showEditor = () => requestAnimationFrame(() => {
    const wrap = editor.current
    if (!wrap) return
    const top = wrap.getBoundingClientRect().top
    if (stacked || top < 0 || top > window.innerHeight - 120) wrap.scrollIntoView({ block: 'start' })
  })

  function pick(w) {
    setAdding(false)
    setSelId(w.id)
    setFlash(null)
    follow(`/admin/webinars/${w.id}`)
    showEditor()
  }

  function startAdding() {
    setAdding(true)
    setFlash(null)
    follow('/admin/webinars')
    showEditor()
  }

  async function added(row, line) {
    await reload()
    if (!alive.current) return
    setAdding(false)
    setSelId(row.id)
    setVersion(v => v + 1)
    setFlash({ id: row.id, text: line })
    follow(`/admin/webinars/${row.id}`)
  }

  return (
    <div className="adc-web">
      <section className="adc-web-side">
        {all.length ? (
          <nav className="adc-list" aria-label={COPY.webinarList}>
            {groups.map(g => (
              <div key={g.key} className="adc-group" role="group" aria-label={g.name}>
                <div className="adc-group-head" aria-hidden="true">{g.name}</div>
                {g.items.map(w => (
                  <button key={w.id} type="button" className="adc-item adc-item--web" aria-current={w.id === sel?.id ? 'true' : undefined} onClick={() => pick(w)}>
                    {anyThumb && (
                      <span className="adc-item-thumb" aria-hidden="true">
                        {preview.url(w) && <img src={preview.url(w)} alt="" decoding="async" onError={e => preview.failed(w, e.currentTarget.src)} onLoad={e => preview.loaded(w, e.currentTarget.src)} />}
                      </span>
                    )}
                    <span className="adc-item-text">
                      <span className="adc-item-name adc-item-name--web"><SellDot product={productBy.get(w.sells_product_id)} />{w.title}</span>
                      <span className="adc-item-sub">{webinarWhen(w)}</span>
                    </span>
                    <span className="adc-item-count">{webinarCount(w, statBy.get(w.id), nowMs)}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
        ) : <div className="ad-card"><EmptyState>{COPY.noWebinars}</EmptyState></div>}
        {edits && (
          <>
            <button type="button" className="adc-dashed adc-dashed--tall" onClick={startAdding} aria-pressed={adding}>
              {COPY.addWebinar}
            </button>
            <span className="adc-side-note">{COPY.addNote}</span>
          </>
        )}
        {(role === 'owner' || role === 'admin') && strategyOpen(ENV.context) && (
          <ButtonLink to={adminPath.strategyCompletions()} size="sm" className="adc-strategy-link">{COPY.stLink}</ButtonLink>
        )}
      </section>
      <div className="adc-editor-wrap" ref={editor}>
        {adding && edits && (
          <WebinarEditor key="new" webinar={null} products={products} role={role} api={api} personId={personId} nowMs={nowMs}
            onAdded={added} onCancel={() => setAdding(false)} />
        )}
        {sel && (
          <WebinarEditor key={`${sel.id}:${version}`} webinar={sel} stat={statBy.get(sel.id)} registered={registeredBy.get(sel.id) ?? 0} products={products} role={role} api={api}
            savedPreview={preview.url(sel)} savedMissing={preview.missing(sel)} onPreviewError={src => preview.failed(sel, src)} onPreviewLoad={src => preview.loaded(sel, src)}
            personId={personId} nowMs={nowMs} flash={flash && flash.id === sel.id ? flash.text : null} clearFlash={() => setFlash(null)}
            onSaved={async line => { await reload(); setVersion(v => v + 1); setFlash({ id: sel.id, text: line }) }} />
        )}
      </div>
    </div>
  )
}

// What a webinar starts from when it's being added: nothing typed, switched off (decision 30)
const BLANK = {
  title: '', description: '', starts_at: null, duration_minutes: NEW_MINUTES, registration_url: null, shown: false,
  sells_product_id: null, cta_label: null, cta_url: null, cta_text: null, replay_url: null, replay_up: false,
  replay_chapters: [], replay_duration_seconds: null, thumbnail_path: null,
}

function WebinarEditor({
  webinar, stat, registered = 0, products, role, api, personId, nowMs, onSaved, onAdded, onCancel, flash, clearFlash,
  savedPreview = null, savedMissing = false, onPreviewError, onPreviewLoad,
}) {
  const isNew = !webinar
  const w = webinar || BLANK
  const edits = OA(role)
  const [title, setTitle] = useState(w.title || '')
  const [description, setDescription] = useState(w.description || '')
  const [day, setDay] = useState(w.starts_at ? ukDayOf(w.starts_at) : '')
  const [at, setAt] = useState(hhmm(w.starts_at))
  const [minutes, setMinutes] = useState(String(w.duration_minutes || NEW_MINUTES))
  const [registration, setRegistration] = useState(w.registration_url || '')
  const [shown, setShown] = useState(Boolean(w.shown))
  const [sells, setSells] = useState(w.sells_product_id || null)
  const [ctaLabel, setCtaLabel] = useState(w.cta_label || '')
  const [ctaUrl, setCtaUrl] = useState(w.cta_url || '')
  const [ctaText, setCtaText] = useState(w.cta_text || '')
  const [replay, setReplay] = useState(w.replay_url || '')
  const [replayUp, setReplayUp] = useState(w.replay_url ? Boolean(w.replay_up) : true)
  const [chapters, setChapters] = useState(chaptersToText(w.replay_chapters))
  // its register page: the link, the name in MailerLite (offered from the link until it's typed) and the switch
  const [regSlug, setRegSlug] = useState(w.register_slug || '')
  const [regName, setRegName] = useState(w.register_list_name || '')
  const [regNameTyped, setRegNameTyped] = useState(Boolean(w.register_list_name))
  const [regOn, setRegOn] = useState(Boolean(w.register_on))
  const [regRefused, setRegRefused] = useState(null)
  const [lengthTyped, setLengthTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  // the thumbnail: its path in the covers bucket (saved with the webinar), and a picked 1's preview
  const [thumbPath, setThumbPath] = useState(w.thumbnail_path || null)
  const [picked, setPicked] = useState(null)
  const [thumbBusy, setThumbBusy] = useState(false)
  const [thumbNote, setThumbNote] = useState(null)
  const [cropped, setCropped] = useState(false)
  // the thumbnail on the screen is the 1 saved on the webinar, shown through its signed link
  const savedThumb = !picked && Boolean(thumbPath) && thumbPath === w.thumbnail_path
  const ids = useId()
  const vimeo = useVimeoRead(replay, { savedUrl: w.replay_url, savedSeconds: w.replay_duration_seconds })
  const touch = setter => v => { setter(v); setProblem(null); clearFlash?.() }
  const options = sellOptions(products)
  const lines = readChapterLines(chapters)
  const bad = lines.filter(l => !l.ok).length
  const hasReplay = Boolean(replay.trim())
  const showLength = vimeo.state === 'failed' || (lengthTyped !== '' && vimeo.state !== 'known')
  const typedStart = day && at ? ukToIso(day, at) : null

  // the chip, the replay note and whether it's past follow what's on the screen, before it's saved, as
  // on the board
  const local = {
    ...w, shown, replay_url: hasReplay ? replay : null, replay_up: replayUp,
    starts_at: typedStart || w.starts_at, duration_minutes: Number(minutes) || w.duration_minutes,
  }
  const past = local.starts_at ? isPast(local, nowMs) : false
  const state = local.starts_at ? webinarState(local, nowMs) : (shown ? 'shown' : 'hidden')
  const replayNote = !hasReplay ? COPY.replayNone : replayUp ? COPY.replayUp : COPY.replayDown
  const noRegistration = showsWithoutRegistration({ shown, past, registration })

  function regSlugTyped(v) {
    const slug = typedSlug(v)
    setRegSlug(slug)
    if (!regNameTyped) setRegName(listNameFromSlug(slug))
    setRegRefused(null); setProblem(null); clearFlash?.()
  }
  function regNameChanged(v) {
    setRegName(v)
    setRegNameTyped(Boolean(v))
    setRegRefused(null); setProblem(null); clearFlash?.()
  }
  // switching on is refused, with the reason, until it has a link, a name and the replay is up
  function regToggle() {
    setProblem(null); clearFlash?.()
    if (!regOn) {
      const why = registerOnProblem({ slug: regSlug, name: regName, hasReplay, replayUp })
      if (why) { setRegRefused(why); return }
    }
    setRegRefused(null)
    setRegOn(!regOn)
  }

  // A picked image: checked, redrawn and uploaded at once, then saved on the webinar with Save
  async function pickThumbnail(file) {
    if (!file || thumbBusy) return
    setProblem(null)
    clearFlash?.()
    const bad = thumbnailFileProblem(file)
    if (bad) return setThumbNote({ text: bad, problem: true })
    setThumbBusy(true)
    setThumbNote(null)
    try {
      let image
      try { image = await shrinkImage(file) } catch { setThumbNote({ text: COPY.thumbnailUnreadable, problem: true }); return }
      const path = await uploadThumbnail(api, { productId: w.product_id || webinarProductOf(products)?.id, image })
      uploaded.set(path, image.preview)
      setThumbPath(path)
      setPicked(image.preview)
      setCropped(!image.sixteenNine)
      setThumbNote({ text: COPY.thumbnailReady(isNew), problem: false })
    } catch (e) {
      setThumbNote({ text: api.words(e), problem: true })
    } finally {
      setThumbBusy(false)
    }
  }

  function takeThumbnailOff() {
    setProblem(null)
    clearFlash?.()
    setThumbPath(null)
    setPicked(null)
    setCropped(false)
    setThumbNote(w.thumbnail_path ? { text: COPY.thumbnailTakenOff, problem: false } : null)
  }

  async function save() {
    if (busy || thumbBusy) return
    setProblem(null)
    clearFlash?.()
    const details = webinarDetailsProblem({ title, description, day, at, registration })
    if (details) return setProblem(details)
    const wrong = webinarProblem({ cta_label: ctaLabel, cta_url: ctaUrl, replay })
    if (wrong) return setProblem(wrong)
    const parsed = chaptersFromText(chapters, w.replay_chapters || [])
    if (parsed.bad) return setProblem(COPY.chaptersNeedTimes)
    if (parsed.duplicate) return setProblem(COPY.chaptersSameTime(parsed.duplicate))
    if (lengthTyped.trim() && !parseLength(lengthTyped)) return setProblem(COPY.lengthBad)
    const regWrong = isNew ? null : registerProblem({ slug: regSlug, name: regName, on: regOn, hasReplay, replayUp })
    if (regWrong) return setProblem(regWrong)
    setBusy(true)
    try {
      let seconds = null
      if (hasReplay) {
        seconds = vimeo.seconds || (await vimeo.settled())?.seconds || parseLength(lengthTyped) || null
        if (vimeo.state === 'known') seconds = vimeo.seconds
      }
      const patch = {
        title: title.trim(),
        description: description.trim() || null,
        starts_at: typedStart,
        duration_minutes: Number(minutes),
        registration_url: registrationLink(registration),
        shown,
        sells_product_id: sells,
        cta_label: ctaLabel.trim() || null,
        cta_url: ctaUrl.trim() || null,
        cta_text: ctaText.trim() || null,
        replay_url: hasReplay ? vimeo.url : null,
        // a replay pasted for the first time goes up; taking it away takes it down
        replay_up: hasReplay ? replayUp : false,
        replay_chapters: parsed.chapters,
        replay_duration_seconds: hasReplay ? seconds : null,
        thumbnail_path: thumbPath,
        // the register page's fields, only those that changed
        ...(isNew ? {} : registerPatch(w, { slug: regSlug, name: regName, on: regOn })),
      }
      // switched on while still to come: whether the database will put it in the bell, asked before the
      // write (afterwards it's already announced)
      const before = isNew ? null : w
      const rings = switchesOnAhead({ before, after: { ...w, ...patch }, nowMs: Date.now() })
        ? await api.bell_will_ring({ kind: 'new_webinar', product: w.product_id || webinarProductOf(products)?.id, item: isNew ? null : w.id })
        : false
      if (isNew) {
        const row = await addWebinar(api, { row: patch, personId })
        await onAdded(row, webinarSavedLine({ before: null, after: row, nowMs: Date.now(), rings }))
      } else {
        const row = await saveWebinar(api, { id: w.id, patch, personId })
        await onSaved(webinarSavedLine({ before: w, after: row, nowMs: Date.now(), rings }))
      }
    } catch (e) {
      setProblem(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  // support reads what's been typed, as the board's box drew it
  const readRows = [
    { key: 'title', label: COPY.zoomRows.title, value: w.title },
    { key: 'description', label: COPY.zoomRows.description, value: w.description || '' },
    { key: 'date', label: COPY.zoomRows.date, value: shortDate(w.starts_at), title: longDate(w.starts_at) },
    { key: 'starts', label: COPY.zoomRows.starts, value: time(w.starts_at) },
    { key: 'length', label: COPY.zoomRows.length, value: duration((w.duration_minutes || 0) * 60) },
    {
      key: 'registration', label: COPY.zoomRows.registration,
      value: past ? COPY.registrationClosed : (w.registration_url
        ? <a href={w.registration_url} target="_blank" rel="noopener noreferrer" className="adc-break">{w.registration_url}</a>
        : COPY.noRegistration),
    },
  ]

  return (
    <section className="ad-card adc-editor adc-web-editor" aria-labelledby={`${ids}-h`}>
      <div className="adc-editor-head">
        <div className="adc-editor-title">
          <span className="adc-eyebrow">{isNew ? COPY.newEyebrow : webinarWhenLong(w)}</span>
          <h2 className="adc-h2" id={`${ids}-h`}>{isNew ? COPY.newHeading : w.title}</h2>
        </div>
        <span className="adc-chip adc-chip--big adc-chip--web">{COPY.webinarStates[state]}</span>
      </div>
      {!edits && <InfoNote>{COPY.webinarReadOnly}</InfoNote>}

      <div className="adc-zoom" role="group" aria-labelledby={`${ids}-z`}>
        <div className="adc-zoom-head">
          <span className="adc-zoom-title" id={`${ids}-z`}>{COPY.detailsBox}</span>
          {edits && <span className="adc-zoom-note">{COPY.detailsNote}</span>}
        </div>
        {edits ? (
          <div className="adc-details">
            <Field label={COPY.zoomRows.title} plain className="adc-detail">
              {props => <TextInput {...props} value={title} onChange={touch(setTitle)} placeholder={COPY.titlePlaceholder} maxLength={200} />}
            </Field>
            <Field label={COPY.zoomRows.description} plain className="adc-detail adc-detail--top">
              {props => <TextArea {...props} value={description} onChange={touch(setDescription)} placeholder={COPY.descriptionPlaceholder} rows={2} maxLength={2000} />}
            </Field>
            <DateField label={COPY.zoomRows.date} value={day} onChange={touch(setDay)} plain className="adc-detail" />
            <TimeField label={COPY.zoomRows.starts} value={at} onChange={v => touch(setAt)(v || '')} plain className="adc-detail" />
            <Field label={COPY.zoomRows.length} plain className="adc-detail">
              {props => <Select {...props} value={minutes} onChange={touch(setMinutes)} options={lengthOptions(w.duration_minutes)} />}
            </Field>
            {past ? (
              <div className="ad-field adc-detail">
                <span className="ad-label ad-label--plain">{COPY.zoomRows.registration}</span>
                <span className="adc-detail-text">{COPY.registrationClosed}</span>
              </div>
            ) : (
              <Field label={COPY.zoomRows.registration} plain className="adc-detail">
                {props => (
                  <TextInput {...props} value={registration} onChange={touch(setRegistration)} placeholder={COPY.registrationPlaceholder}
                    inputMode="url" autoComplete="off" spellCheck={false} />
                )}
              </Field>
            )}
          </div>
        ) : (
          <dl className="adc-zoom-rows">
            {readRows.map(r => (
              <div key={r.key} className="adc-zoom-row">
                <dt>{r.label}</dt>
                <dd title={r.title}>{r.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <Thumbnail ids={ids} edits={edits} path={thumbPath} url={picked || (savedThumb ? savedPreview : null)} missing={savedThumb && savedMissing}
        when={typedStart || w.starts_at} busy={thumbBusy} note={thumbNote} cropped={cropped} onCropped={setCropped}
        onPick={pickThumbnail} onTakeOff={takeThumbnailOff} onError={savedThumb ? onPreviewError : undefined} onLoad={savedThumb ? onPreviewLoad : undefined} />

      <div className="adc-show">
        <button type="button" role="switch" aria-checked={shown} className="adc-showrow" disabled={!edits}
          aria-describedby={noRegistration ? `${ids}-noreg` : undefined} onClick={() => touch(setShown)(!shown)}>
          <span className="adc-showrow-text">
            <span className="adc-showrow-title">{COPY.showIt}</span>
            <span className="adc-showrow-note">{shown ? COPY.shownNote : COPY.hiddenNote}</span>
          </span>
          <span className="adc-track" aria-hidden="true"><span className="adc-knob" /></span>
        </button>
        {noRegistration && <p className="adc-show-warn" id={`${ids}-noreg`}>{COPY.noRegistrationHint}</p>}
      </div>

      <div className="adc-sells">
        <span className="adc-field-label" id={`${ids}-s`}>{COPY.sells}</span>
        <div className="adc-sell-chips" role="radiogroup" aria-labelledby={`${ids}-s`}>
          {options.map(o => (
            <button key={o.value || 'nothing'} type="button" role="radio" aria-checked={sells === o.value} className="adc-sell"
              disabled={!edits} onClick={() => touch(setSells)(o.value)}>
              <SellDot product={o.product} />{o.label}
            </button>
          ))}
        </div>
        <span className="adc-hint">{COPY.sellsNote}</span>
      </div>

      <div className="adc-two">
        <Field label={COPY.ctaLabel} plain>
          {props => <TextInput {...props} value={ctaLabel} onChange={touch(setCtaLabel)} placeholder={COPY.ctaLabelPlaceholder} disabled={!edits} />}
        </Field>
        <Field label={COPY.ctaUrl} plain>
          {props => <TextInput {...props} value={ctaUrl} onChange={touch(setCtaUrl)} placeholder={COPY.ctaUrlPlaceholder} disabled={!edits} inputMode="url" autoComplete="off" spellCheck={false} />}
        </Field>
      </div>
      <Field label={COPY.ctaText} plain>
        {props => <TextInput {...props} value={ctaText} onChange={touch(setCtaText)} placeholder={COPY.ctaTextPlaceholder} disabled={!edits} />}
      </Field>

      <div className="adc-replay">
        <Field label={COPY.replay} plain>
          {props => (
            <TextInput {...props} value={replay} onChange={touch(setReplay)} placeholder={COPY.replayPlaceholder} disabled={!edits}
              aria-describedby={`${ids}-rn`} autoComplete="off" spellCheck={false} />
          )}
        </Field>
        {hasReplay && <LinkNote link={vimeo} id={`${ids}-rn`} />}
        {showLength && edits && (
          <Field label={COPY.length} hint={COPY.lengthHint} plain className="adc-length">
            {props => <TextInput {...props} value={lengthTyped} onChange={touch(setLengthTyped)} placeholder={COPY.lengthPlaceholder} inputMode="numeric" />}
          </Field>
        )}
        <div className="adc-replay-row">
          <span className="adc-replay-note">{replayNote}</span>
          {hasReplay && edits && (
            <Button size="sm" onClick={() => touch(setReplayUp)(!replayUp)}>{replayUp ? COPY.takeDown : COPY.putBack}</Button>
          )}
        </div>
        {(hasReplay || chapters) && (
          <Field label={COPY.replayChapters} plain>
            {props => <TextArea {...props} className="adc-chapters" value={chapters} onChange={touch(setChapters)} placeholder={COPY.chaptersPlaceholder} rows={5} disabled={!edits} spellCheck={false} />}
          </Field>
        )}
        {(hasReplay || chapters) && <span className="adc-count">{COPY.chapterCount(lines.length, bad)}</span>}
      </div>

      {!isNew && (
        <RegisterPart ids={ids} edits={edits} oa={edits} webinarId={w.id} savedSlug={w.register_slug || null} count={registered}
          slug={regSlug} name={regName} on={regOn} refused={regRefused} notShown={!shown}
          onSlug={regSlugTyped} onName={regNameChanged} onToggle={regToggle} />
      )}

      {!isNew && (
        <div className="adc-tiles">
          {webinarTiles(w, stat, nowMs).map(t => (
            <div key={t.key} className="adc-tile">
              <span className="adc-tile-label">{t.label}</span>
              <span className="adc-tile-value">{t.value}</span>
              <span className="adc-tile-note">{t.note}</span>
            </div>
          ))}
        </div>
      )}

      {(edits || problem) && (
        <div className="adc-dock">
          {edits && (
            <div className="adc-foot adc-foot--start">
              <Button kind="ink" busy={busy} disabled={thumbBusy} onClick={save}>{isNew ? COPY.addIt : COPY.webinarSave}</Button>
              {isNew && <Button onClick={onCancel} disabled={busy}>{COPY.cancelAdd}</Button>}
              {flash && <span className="adc-flash" role="status">{flash}</span>}
            </div>
          )}
          {problem && <p className="ad-error" role="alert">{problem}</p>}
        </div>
      )}
    </section>
  )
}
